import React from 'react';
import { DataConnectionType, DataTypeValue, SourceColumn, SourceObject } from '../types';
import { getSourceObjectDisplay, getSourceObjectKey } from '../utilities';
import ColumnSelectionGrid from './ColumnSelectionGrid';

interface DataSchemaStepProps {
    type?: DataConnectionType;
    isLinuxHost: boolean;
    sourceObjects: SourceObject[];
    selectedSourceKey: string;
    columns: SourceColumn[];
    selectedColumnKeys: string[];
    dataTypesByColumnKey: Record<string, DataTypeValue>;
    errors: Record<string, string>;
    discoveringSources: boolean;
    discoveringColumns: boolean;
    sourcesLoadedFromCurrentConnection: boolean;
    columnsLoadedFromCurrentSource: boolean;
    onSourceChange: (sourceKey: string) => void;
    onToggleColumn: (columnKey: string, selected: boolean) => void;
    onDataTypeChange: (columnKey: string, dataType: DataTypeValue) => void;
}

const DataSchemaStep: React.FC<DataSchemaStepProps> = ({
    type,
    isLinuxHost,
    sourceObjects,
    selectedSourceKey,
    columns,
    selectedColumnKeys,
    dataTypesByColumnKey,
    errors,
    discoveringSources,
    discoveringColumns,
    sourcesLoadedFromCurrentConnection,
    columnsLoadedFromCurrentSource,
    onSourceChange,
    onToggleColumn,
    onDataTypeChange,
}) => {
    const selectedSource = sourceObjects.find(source => getSourceObjectKey(source) === selectedSourceKey);
    const showSourceSelect = sourceObjects.length !== 1;
    const loadingColumns = Boolean(selectedSource) && !columnsLoadedFromCurrentSource;
    const loadingMetadata = discoveringSources || discoveringColumns || !sourcesLoadedFromCurrentConnection || loadingColumns;
    const emptyMessage = loadingMetadata
        ? InovxLoader.t('facility:dataSources.loadingData')
        : InovxLoader.t('facility:dataSources.noColumnsDiscovered');
    const sourceLabel = type === DataConnectionType.Excel
        ? InovxLoader.t('facility:dataSources.fields.selectSheet')
        : InovxLoader.t('facility:dataSources.fields.selectTableView');

    return (
        <div className="data-sources-step data-sources-schema-step">
            {isLinuxHost && type === DataConnectionType.MsAccess && (
                <div className="data-sources-schema-warning" role="note">
                    {InovxLoader.t('facility:dataSources.accessCalculatedColumnsWarning')}
                </div>
            )}
            {showSourceSelect ? (
                <label className="data-sources-field data-sources-field-inline data-sources-source-select">
                    <span>{sourceLabel}</span>
                    <select className={errors.source ? 'invalid' : ''} value={selectedSourceKey} onChange={event => onSourceChange(event.target.value)}>
                        <option value="">{InovxLoader.t('facility:dataSources.placeholders.selectSourceObject')}</option>
                        {sourceObjects.map(source => (
                            <option key={getSourceObjectKey(source)} value={getSourceObjectKey(source)}>
                                {getSourceObjectDisplay(source)}
                            </option>
                        ))}
                    </select>
                    {errors.source && <span className="data-sources-field-error">{errors.source}</span>}
                </label>
            ) : (
                <label className="data-sources-field data-sources-field-inline data-sources-source-select">
                    <span>{sourceLabel}</span>
                    <input type="text" value={selectedSource ? getSourceObjectDisplay(selectedSource) : ''} readOnly={true} />
                    {errors.source && <span className="data-sources-field-error">{errors.source}</span>}
                </label>
            )}
            {columns.length > 0 && columnsLoadedFromCurrentSource ? (
                <ColumnSelectionGrid
                    providerType={type}
                    columns={columns}
                    selectedColumnKeys={selectedColumnKeys}
                    dataTypesByColumnKey={dataTypesByColumnKey}
                    onToggleColumn={onToggleColumn}
                    onDataTypeChange={onDataTypeChange}
                />
            ) : (
                <div className="data-sources-empty-inline">{emptyMessage}</div>
            )}
            {errors.columns && <div className="data-sources-step-error">{errors.columns}</div>}
        </div>
    );
};

export default DataSchemaStep;
